import { redirect } from '@tanstack/react-router';
import type Keycloak from 'keycloak-js';

/**
 * Route guards for TanStack Router integration.
 * These guards can be used in route definitions with beforeLoad.
 */
export interface RouteGuards {
  /** Requires user to be authenticated */
  requireAuthentication: () => void;
  /** Requires user to have specific roles */
  requireRoles: (roles: string[]) => () => void;
}

/**
 * Creates route guards that can be used with TanStack Router's beforeLoad.
 * This factory pattern eliminates the need for global state.
 *
 * @param keycloak - Initialized Keycloak instance
 * @returns Object containing route guard functions
 */
export function createRouteGuards(keycloak: Keycloak): RouteGuards {
  /**
   * Redirects to login if user is not authenticated
   */
  const requireAuthentication = () => {
    const authenticated = keycloak.authenticated ?? false;

    if (!authenticated) {
      throw redirect({
        to: '/',
        search: {
          redirectReason: 'not_authenticated' as const,
        },
      });
    }
  };

  /**
   * Creates a role-based authorization guard.
   * Checks realm roles, matching backend token authorization semantics.
   *
   * @param requiredRoles - Array of roles that user must have (any one of them)
   * @returns Guard function that throws redirect if user lacks required roles
   */
  const requireRoles = (requiredRoles: string[]) => {
    return () => {
      // First ensure user is authenticated
      requireAuthentication();

      // Check if user has any of the required roles
      const hasRequiredRole = requiredRoles.some((role) =>
        keycloak.hasRealmRole(role),
      );

      if (!hasRequiredRole) {
        throw redirect({
          to: '/',
          search: {
            redirectReason: 'insufficient_permissions' as const,
          },
        });
      }
    };
  };

  return {
    requireAuthentication,
    requireRoles,
  };
}
